import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Tag } from "antd";
import {
    useGetConnectionSequenceStepLogsQuery,
    useGetConnectionSequenceStepLogsExportMutation,
} from "api/TenantSlug/ConnectionSequenceStepLogs/hooks.ts";
import { LogConnectionDrawerDetail } from "components/Drawers/Logs/LogConnectionDrawerDetail.tsx";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom";
import { FILTER_TYPES } from "components/UI/Table/constants/filter";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table";
import { ApiResponseBodyCell } from "components/UI/Table/UI/ApiResponseBodyCell/ApiResponseBodyCell.tsx";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell.tsx";
import { useCreatedAtFilterValue } from "components/UI/Table/utils/getDefaultCreatedAtFilter.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useSmartRowHandlers } from "hooks/useTableRowHandlers.tsx";
import { Key } from "react";
import { useRecoilValue } from "recoil";
import { ConnectionSequenceStepLogModelType, PermissionEnum } from "types/enums";
import { DrawerId, TableId } from "types/models";

import ConnectionSequenceStepLogData = App.Data.ConnectionSequenceStepLogData;
import GetConnectionSequenceStepLogsResponse = App.Http.Responses.ConnectionSequenceStepLog.GetConnectionSequenceStepLogsResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;

export const StructureLogsTable = () => {
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tGL } = usePaldockTranslation("tables.general-labels");
    const { t } = usePaldockTranslation("tables.structure-logs");
    const tableId = TableId.StructureLogsTable;
    const tableAtomInstance = tableAtom<ConnectionSequenceStepLogData>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const exportState = useRecoilValue(exportAtom(tableId));
    const createdAtStr = useCreatedAtFilterValue();

    const request = {
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
        filter: {
            filter: {
                model_type: ConnectionSequenceStepLogModelType.FormStructure,
                ...(tableState.filter?.filter || {}),
                ...(tableState.filter?.filter?.created_at
                    ? {}
                    : {
                          created_at: {
                              btw: createdAtStr,
                          },
                      }),
            },
            sort: tableState?.filter?.sort ?? "-created_at",
            include: "connectionSequenceStep,offer,conversionState,owner,advertiser,cascade,integration",
        },
    };

    const { getRowHandlers, getRowClassName } = useSmartRowHandlers<ConnectionSequenceStepLogData>({
        entityName: "connectionSequenceStepLog",
        detailPermission: PermissionEnum.LogShowDetail,
        detailDrawerId: DrawerId.ConnectionSequenceStepLog,
        detailDrawerParam: DrawerId.ConnectionSequenceStepLog,
    });

    const getConnectionSequenceStepLogsQuery = useGetConnectionSequenceStepLogsQuery(request, { enabled: false });
    const getConnectionSequenceStepLogsExportMutation = useGetConnectionSequenceStepLogsExportMutation();

    const tableFetchDataUtils = useTableDataApiOperations<
        GetConnectionSequenceStepLogsResponse,
        void,
        Record<string, never>,
        BaseExportRequest
    >(tableId, getConnectionSequenceStepLogsQuery, undefined, undefined, getConnectionSequenceStepLogsExportMutation);

    const permissionsUtils = usePermission();

    const columnConfig: TableColumnType<ConnectionSequenceStepLogData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={tG("date")} />,
            dataIndex: "created_at",
            key: "created_at",
            inputType: "text",
            render: (v) => <DateTimeCell value={v} />,
            sorter: true,
            defaultSorter: true,
            defaultSortOrder: "descend",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("conversion_id")} />,
            dataIndex: "conversionState.origin_eid",
            key: "conversionState.origin_eid",
            inputType: "text",
            render: (_, data) =>
                data.conversionState?.origin_eid ? (
                    <ShortenedIdWithCopy id={data.conversionState.origin_eid ?? undefined} />
                ) : (
                    "-"
                ),
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "conversionState.origin_eid",
            sorter: true,
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("event_id")} />,
            dataIndex: "conversionState.eid",
            key: "conversionState.eid",
            inputType: "text",
            render: (_, data) =>
                data.conversionState?.eid ? <ShortenedIdWithCopy id={data.conversionState.eid} /> : "-",
            filterable: true,
            filterType: FILTER_TYPES.eid,
            sorter: true,
            width: 150,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("structure")} />,
            dataIndex: "structure",
            key: "structure",
            inputType: "text",
            render: () => "-", // TODO: BE needed
            filterType: FILTER_TYPES.string,
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.field")} />,
            dataIndex: "field",
            key: "field",
            inputType: "text",
            render: () => "-", // TODO: BE needed
            filterType: FILTER_TYPES.string,
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("type")} />,
            dataIndex: "connectionSequenceStep.type",
            key: "connectionSequenceStep.type",
            inputType: "text",
            render: (_, result) => result.connectionSequenceStep?.type ?? "-",
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "connectionSequenceStep.type",
            sorter: true,
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("method_name")} />,
            dataIndex: "request_name",
            key: "request_name",
            inputType: "text",
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "request_name",
            sorter: true,
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("status")} />,
            dataIndex: "http_status",
            key: "http_status",
            inputType: "text",
            filterable: true,
            filterType: FILTER_TYPES.number,
            filterName: "http_status",
            sorter: true,
            ellipsis: true,
            width: 100,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("state")} />,
            dataIndex: "is_success",
            key: "status",
            render: (is_success) =>
                is_success ? (
                    <Tag icon={<CheckCircleOutlined />} color="green">
                        {tGL("ok")}
                    </Tag>
                ) : (
                    <Tag icon={<CloseCircleOutlined />} color="red">
                        {tGL("fail")}
                    </Tag>
                ),
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterName: "is_success",
            filterOptions: [
                { label: tGL("ok"), value: true },
                { label: tGL("fail"), value: false },
            ],
            sorter: true,
            ellipsis: true,
            width: 90,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("response")} />,
            dataIndex: "response",
            key: "response",
            inputType: "text",
            render: (v, row) => {
                const id = `response-${row.id}`;
                const parsedValue = JSON.parse(v);
                return <ApiResponseBodyCell response={parsedValue} id={id} />;
            },
            filterType: FILTER_TYPES.string,
            filterName: "response",
            className: "overflow-hidden",
            ellipsis: true,
            width: 350,
        },
    ];

    return (
        <>
            <Table<GetConnectionSequenceStepLogsResponse, ConnectionSequenceStepLogData>
                tableId={tableId}
                data={tableFetchDataUtils.tableData as GetConnectionSequenceStepLogsResponse}
                dataSource={tableFetchDataUtils.tableData?.connectionSequenceStepLogs || []}
                columnConfig={columnConfig}
                loading={getConnectionSequenceStepLogsQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                onExport={() =>
                    tableFetchDataUtils.exportData({
                        extension: exportState.extension,
                        headers: exportState.headers,
                        export_all: exportState.range === "all",
                        page: request.page,
                        filter: request.filter,
                        delimiter: exportState.delimiter,
                    })
                }
                rowKey={(record) => record.id as Key}
                enableFilters={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilter)}
                enableColumnsVisibility={permissionsUtils.hasPermission(PermissionEnum.LogColumnSetting)}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                enableTimeFilter={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilterTime)}
                dateFilterMode={"minute"}
                hideActionsColumn={true}
                dateFilterDataIndex={"created_at"}
                autoScrollWidth={true}
            />
            <LogConnectionDrawerDetail type={ConnectionSequenceStepLogModelType.FormStructure} />
        </>
    );
};
